import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { callEdgeFunction } from "@/lib/edge";
import { formatError } from "@/ui/data/async";
import type {
  MythicCharacterBundle,
  MythicQuestThreadRow,
  MythicCharacterRow,
  MythicProgressionEventRow,
  MythicSkill,
} from "@/types/mythic";

type LensKey = "offense" | "defense" | "mobility" | "control" | "support" | "utility";

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function safeString(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim().length > 0 ? value : fallback;
}

function summarizePayload(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const raw = payload as Record<string, unknown>;
  const keys = ["summary", "description", "reason", "note", "objective", "type"];
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "string" && value.trim().length > 0) return value.trim();
  }
  return "";
}

function normalizeReasonLabel(value: string): string {
  const raw = value
    .replace(/^narrative:/i, "")
    .replace(/^fallback-/i, "")
    .replace(/^dm-action-/i, "")
    .replace(/^transition_reason:/i, "")
    .trim();
  if (!raw) return "Story Progression";
  return raw
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function useMythicCharacter(campaignId: string | undefined) {
  const [bundle, setBundle] = useState<MythicCharacterBundle | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const pendingRefetchRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const fetchBundle = useCallback(async () => {
    if (!campaignId || !isUuid(campaignId)) {
      if (isMountedRef.current) {
        setIsInitialLoading(false);
        setIsRefreshing(false);
        setBundle(null);
        setError(null);
      }
      hasLoadedOnceRef.current = false;
      return;
    }

    if (inFlightRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    try {
      inFlightRef.current = true;
      if (isMountedRef.current) {
        if (hasLoadedOnceRef.current) {
          setIsRefreshing(true);
        } else {
          setIsInitialLoading(true);
        }
        setError(null);
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        if (isMountedRef.current) {
          setIsInitialLoading(false);
          setIsRefreshing(false);
          setBundle(null);
        }
        hasLoadedOnceRef.current = true;
        return;
      }

      const { data: character, error: charError } = await supabase
        .schema("mythic")
        .from("characters")
        .select("*")
        .eq("campaign_id", campaignId)
        .eq("player_id", user.id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (charError) throw charError;
      if (!character) {
        if (isMountedRef.current) {
          setBundle(null);
        }
        return;
      }

      const { data: skills, error: skillsError } = await supabase
        .schema("mythic")
        .from("skills")
        .select("*")
        .eq("character_id", (character as MythicCharacterRow).id)
        .order("created_at", { ascending: true });

      if (skillsError) throw skillsError;

      const { data: inv, error: invError } = await supabase
        .schema("mythic")
        .from("inventory")
        .select("id, container, equip_slot, quantity, equipped_at, item:items(*)")
        .eq("character_id", (character as MythicCharacterRow).id)
        .order("created_at", { ascending: true });

      if (invError) throw invError;

      const mythic = supabase.schema("mythic") as any;

      const [
        { data: progressionEvents, error: progressionError },
        { data: memoryEvents, error: memoryError },
        { data: runtimeTransitions, error: transitionsError },
        { data: lootDrops, error: lootError },
        { data: reputationEvents, error: reputationError },
        { data: factions, error: factionsError },
      ] =
        await Promise.all([
          supabase
            .schema("mythic")
            .from("progression_events")
            .select("id,campaign_id,character_id,event_type,payload,created_at")
            .eq("character_id", (character as MythicCharacterRow).id)
            .order("created_at", { ascending: false })
            .limit(20),
          supabase
            .schema("mythic")
            .from("dm_memory_events")
            .select("id,category,severity,payload,created_at,player_id")
            .eq("campaign_id", campaignId)
            .or(`player_id.eq.${user.id},player_id.is.null`)
            .order("created_at", { ascending: false })
            .limit(30),
          mythic
            .from("runtime_events")
            .select("id,from_mode,to_mode,reason,payload_json,created_at")
            .eq("campaign_id", campaignId)
            .order("created_at", { ascending: false })
            .limit(30),
          supabase
            .schema("mythic")
            .from("loot_drops")
            .select("id,rarity,source,payload,created_at,item_ids")
            .eq("campaign_id", campaignId)
            .order("created_at", { ascending: false })
            .limit(24),
          supabase
            .schema("mythic")
            .from("reputation_events")
            .select("id,faction_id,severity,delta,evidence,occurred_at,player_id")
            .eq("campaign_id", campaignId)
            .or(`player_id.eq.${user.id},player_id.is.null`)
            .order("occurred_at", { ascending: false })
            .limit(24),
          supabase
            .schema("mythic")
            .from("factions")
            .select("id,name")
            .eq("campaign_id", campaignId),
        ]);

      if (progressionError) throw progressionError;
      if (memoryError) throw memoryError;
      if (transitionsError) throw transitionsError;
      if (lootError) throw lootError;
      if (reputationError) throw reputationError;
      if (factionsError) throw factionsError;

      const factionNameById = new Map<string, string>(
        (factions ?? [])
          .filter((row): row is { id: string; name: string } => typeof row.id === "string" && typeof row.name === "string")
          .map((row) => [row.id, row.name]),
      );

      const questThreads: MythicQuestThreadRow[] = [];

      for (const event of (memoryEvents ?? []) as Array<Record<string, unknown>>) {
        const id = safeString(event.id, crypto.randomUUID());
        const category = safeString(event.category, "memory");
        const payload = event.payload && typeof event.payload === "object" ? event.payload as Record<string, unknown> : {};
        const companionCheckin = payload.companion_checkin && typeof payload.companion_checkin === "object"
          ? payload.companion_checkin as Record<string, unknown>
          : null;
        if (category === "companion_checkin" && companionCheckin) {
          const line = safeString(companionCheckin.line);
          const companionId = safeString(companionCheckin.companion_id, "companion");
          const title = line.includes(":")
            ? line.split(":")[0]!.trim()
            : `Companion ${companionId}`;
          questThreads.push({
            id: `memory:${id}`,
            source: "dm_memory",
            event_type: "companion_checkin",
            title: `Companion Check-In · ${title}`,
            detail: line || summarizePayload(payload),
            severity: Number(event.severity ?? 1) || 1,
            created_at: safeString(event.created_at),
          });
          continue;
        }
        questThreads.push({
          id: `memory:${id}`,
          source: "dm_memory",
          event_type: category,
          title: safeString(payload.title, category.replace(/_/g, " ")),
          detail: summarizePayload(payload),
          severity: Number(event.severity ?? 1) || 1,
          created_at: safeString(event.created_at),
        });
      }

      for (const transition of (runtimeTransitions ?? []) as Array<Record<string, unknown>>) {
        const id = safeString(transition.id, crypto.randomUUID());
        const fromBoard = safeString(transition.from_mode ?? transition.from_board_type, "?").toUpperCase();
        const toBoard = safeString(transition.to_mode ?? transition.to_board_type, "?").toUpperCase();
        const payload = transition.payload_json && typeof transition.payload_json === "object"
          ? transition.payload_json as Record<string, unknown>
          : {};
        const detailParts: string[] = [];
        const reason = normalizeReasonLabel(safeString(transition.reason));
        if (reason) detailParts.push(`Reason: ${reason}`);
        const reasonCode = safeString(payload.reason_code);
        if (reasonCode) detailParts.push(`Code: ${reasonCode}`);
        const travelGoal = safeString(payload.travel_goal);
        if (travelGoal) detailParts.push(`Goal: ${travelGoal.replace(/_/g, " ")}`);
        const searchTarget = safeString(payload.search_target);
        if (searchTarget) detailParts.push(`Target: ${searchTarget}`);
        questThreads.push({
          id: `transition:${id}`,
          source: "runtime_transition",
          event_type: "runtime_transition",
          title: `${fromBoard} -> ${toBoard}`,
          detail: detailParts.join(" · "),
          severity: 1,
          created_at: safeString(transition.created_at),
        });
      }

      for (const event of (progressionEvents ?? []) as Array<Record<string, unknown>>) {
        const id = safeString(event.id, crypto.randomUUID());
        const payload = event.payload && typeof event.payload === "object" ? event.payload as Record<string, unknown> : {};
        const eventType = safeString(event.event_type, "progression");
        questThreads.push({
          id: `progression:${id}`,
          source: "progression",
          event_type: eventType,
          title: eventType.replace(/_/g, " "),
          detail: summarizePayload(payload),
          severity: eventType === "level_up" ? 2 : 1,
          created_at: safeString(event.created_at),
        });
      }

      for (const drop of (lootDrops ?? []) as Array<Record<string, unknown>>) {
        const id = safeString(drop.id, crypto.randomUUID());
        const payload = drop.payload && typeof drop.payload === "object" ? drop.payload as Record<string, unknown> : {};
        const rarity = safeString(drop.rarity, "loot");
        const itemIds = Array.isArray(drop.item_ids) ? drop.item_ids.length : 0;
        questThreads.push({
          id: `loot:${id}`,
          source: "loot_drop",
          event_type: safeString(drop.source, "loot_drop"),
          title: `Loot Drop · ${rarity}`,
          detail: summarizePayload(payload) || `${itemIds} item${itemIds === 1 ? "" : "s"} added.`,
          severity: rarity === "mythic" || rarity === "legendary" ? 3 : 2,
          created_at: safeString(drop.created_at),
        });
      }

      for (const reputation of (reputationEvents ?? []) as Array<Record<string, unknown>>) {
        const id = safeString(reputation.id, crypto.randomUUID());
        const delta = Number(reputation.delta ?? 0);
        const factionId = safeString(reputation.faction_id);
        const factionName = factionNameById.get(factionId) ?? "Faction";
        const evidence = reputation.evidence && typeof reputation.evidence === "object"
          ? reputation.evidence as Record<string, unknown>
          : {};
        questThreads.push({
          id: `reputation:${id}`,
          source: "reputation",
          event_type: "reputation_shift",
          title: `${factionName}: ${delta >= 0 ? "+" : ""}${Math.floor(delta)}`,
          detail: summarizePayload(evidence),
          severity: Number(reputation.severity ?? 1) || 1,
          created_at: safeString(reputation.occurred_at),
        });
      }

      const sortedThreads = questThreads
        .filter((entry) => entry.created_at.length > 0)
        .sort((a, b) => {
          const bMs = Number.isFinite(new Date(b.created_at).getTime()) ? new Date(b.created_at).getTime() : 0;
          const aMs = Number.isFinite(new Date(a.created_at).getTime()) ? new Date(a.created_at).getTime() : 0;
          return bMs - aMs;
        })
        .slice(0, 80);

      if (isMountedRef.current) {
        setBundle({
          character: character as MythicCharacterRow,
          skills: (skills ?? []) as unknown as MythicSkill[],
          items: ((inv ?? []).map((row) => row)) as unknown as Array<Record<string, unknown>>,
          progressionEvents: (progressionEvents ?? []) as unknown as MythicProgressionEventRow[],
          questThreads: sortedThreads,
        });
      }
    } catch (e) {
      const msg = formatError(e, "Failed to load mythic character");
      if (isMountedRef.current) setError(msg);
    } finally {
      inFlightRef.current = false;
      const shouldRefetchAgain = pendingRefetchRef.current && isMountedRef.current;
      if (shouldRefetchAgain) {
        pendingRefetchRef.current = false;
        void fetchBundle();
      }
      if (isMountedRef.current && !shouldRefetchAgain) {
        hasLoadedOnceRef.current = true;
        setIsInitialLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [campaignId]);

  const updateCharacterProfile = useCallback(async (args: {
    characterId: string;
    name: string;
    callsign: string;
    pronouns: string;
    origin_note: string;
  }) => {
    const current = bundle?.character;
    if (!current) throw new Error("No character loaded.");
    if (current.id !== args.characterId) throw new Error("Character context changed. Refresh and retry.");

    const classJson = current.class_json && typeof current.class_json === "object"
      ? { ...current.class_json as Record<string, unknown> }
      : {};
    const existingProfile = classJson.profile && typeof classJson.profile === "object"
      ? classJson.profile as Record<string, unknown>
      : {};
    const nextClassJson: Record<string, unknown> = {
      ...classJson,
      profile: {
        ...existingProfile,
        callsign: args.callsign,
        pronouns: args.pronouns,
        origin_note: args.origin_note,
      },
    };

    const { data, error } = await supabase
      .schema("mythic")
      .from("characters")
      .update({
        name: args.name,
        class_json: nextClassJson as any,
      })
      .eq("id", args.characterId)
      .select("*")
      .single();

    if (error) throw error;
    if (!data) throw new Error("Character update returned empty response.");

    const updatedCharacter = data as unknown as MythicCharacterRow;
    if (isMountedRef.current) {
      setBundle((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          character: updatedCharacter,
        };
      });
    }

    return updatedCharacter;
  }, [bundle?.character]);

  const spendPoints = useCallback(async (args: {
    characterId?: string;
    allocations: Partial<Record<LensKey, number>>;
    reason?: string;
  }) => {
    if (!campaignId || !isUuid(campaignId)) {
      throw new Error("Campaign context is missing.");
    }

    const compactAllocations: Partial<Record<LensKey, number>> = {};
    for (const key of ["offense", "defense", "mobility", "control", "support", "utility"] as const) {
      const value = Number(args.allocations[key] ?? 0);
      if (!Number.isFinite(value) || value <= 0) continue;
      compactAllocations[key] = Math.max(0, Math.floor(value));
    }

    if (Object.keys(compactAllocations).length === 0) {
      throw new Error("At least one positive allocation is required.");
    }

    const { data, error } = await callEdgeFunction<{ ok: boolean; result?: Record<string, unknown> }>("mythic-spend-points", {
      requireAuth: true,
      body: {
        campaignId,
        characterId: args.characterId,
        allocations: compactAllocations,
        reason: args.reason,
      },
    });
    if (error) throw error;
    if (!data?.ok) {
      throw new Error("Failed to apply point allocation.");
    }
    await fetchBundle();
    return data.result ?? null;
  }, [campaignId, fetchBundle]);

  useEffect(() => {
    isMountedRef.current = true;
    if (campaignId && isUuid(campaignId)) {
      hasLoadedOnceRef.current = false;
      setIsInitialLoading(true);
      setIsRefreshing(false);
    }
    fetchBundle();
    return () => {
      isMountedRef.current = false;
    };
  }, [campaignId, fetchBundle]);

  return {
    bundle,
    character: bundle?.character ?? null,
    skills: bundle?.skills ?? [],
    items: bundle?.items ?? [],
    progressionEvents: bundle?.progressionEvents ?? [],
    questThreads: bundle?.questThreads ?? [],
    isInitialLoading,
    isRefreshing,
    isLoading: isInitialLoading,
    error,
    refetch: fetchBundle,
    updateCharacterProfile,
    spendPoints,
  };
}
